import { useEffect, useRef, useState } from "react";
import {
  useCreateOperationsNoteMutation,
  useLazyGetOperationsNotesQuery,
  useUpdateOperationsNoteMutation,
  useDeleteOperationsNoteMutation,
} from "../../store/apiSlice/operationsApiSlice";
import { NotesDrawer } from "../../app/newLayout/page/components/Notes/NotesDrawer";
import { NotesList } from "../../app/newLayout/page/components/Notes/NotesList";
import { TaskComposer } from "../../app/newLayout/page/components/Notes/TaskComposer";
import {
  EmptyState, Filters, Layout, LedgerHeader, LedgerTitle, ScrollRegion, UndoBar,
} from "../../app/newLayout/page/components/Notes/NotesComposer.styles";
import { dueUrgency } from "../../app/newLayout/page/components/Notes/taskDue";

const FILTERS = [
  { id: "all", label: "All Tasks" },
  { id: "due", label: "Due Tasks" },
  { id: "overdue", label: "Overdue" },
];

export function OperationsNotesDrawer({ onClose }) {
  const [loadNotes] = useLazyGetOperationsNotesQuery();
  const [items, setItems] = useState([]);
  const [nextOffset, setNextOffset] = useState(null);
  const [count, setCount] = useState(0);
  const [openCount, setOpenCount] = useState(0);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saveError, setSaveError] = useState(false);
  const [notice, setNotice] = useState(null);
  const [createNote, { isLoading: saving }] = useCreateOperationsNoteMutation();
  const [updateNote, { isLoading: updating }] = useUpdateOperationsNoteMutation();
  const [deleteNote] = useDeleteOperationsNoteMutation();
  const requestId = useRef(0);
  const deletingIds = useRef(new Set());
  const noticeTimer = useRef(null);

  const showNotice = nextNotice => {
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    setNotice(nextNotice);
    noticeTimer.current = window.setTimeout(() => setNotice(null), 8000);
  };

  const loadPage = async (offset, nextFilter = filter) => {
    const currentRequest = ++requestId.current;
    setLoading(true);
    setError(false);
    try {
      const page = await loadNotes({
        offset,
        dueOnly: nextFilter === "due",
        overdueOnly: nextFilter === "overdue",
      }).unwrap();
      if (currentRequest !== requestId.current) return;
      const pendingTasks = page.items.filter(item => item.is_task && !item.is_done);
      setItems(previous => (offset ? [...previous, ...pendingTasks] : pendingTasks));
      setNextOffset(page.next_offset);
      setCount(page.count);
      if (nextFilter === "all") setOpenCount(page.count);
    } catch {
      if (currentRequest === requestId.current) setError(true);
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  };

  const selectFilter = nextFilter => {
    if (nextFilter === filter) return;
    setFilter(nextFilter);
    setItems([]);
    setCount(0);
    setNextOffset(null);
    loadPage(0, nextFilter);
  };

  const recordPayload = records =>
    records?.length ? { records: records.map(({ type, id }) => ({ type, id })) } : {};

  const matchesFilter = item => {
    if (!(item.is_task && !item.is_done)) return false;
    if (filter === "due") return Boolean(item.due_date);
    if (filter === "overdue") return dueUrgency(item.due_date) === "overdue";
    return true;
  };

  const saveTask = async ({ records, body, editingItem }) => {
    let saved;
    if (editingItem) {
      saved = await updateNote({
        id: editingItem.id,
        body: { ...body, ...recordPayload(records) },
      }).unwrap();
    } else {
      saved = await createNote({ ...body, ...recordPayload(records) }).unwrap();
    }
    setEditItem(null);
    if (!saved?.id) {
      await loadPage(0);
      return;
    }
    const item = { ...saved, created_at: saved.date_of_creation };
    const showItem = matchesFilter(item);
    if (editingItem) {
      const wasOpen = editingItem.is_task && !editingItem.is_done;
      const isOpen = item.is_task && !item.is_done;
      if (wasOpen !== isOpen) setOpenCount(previous => Math.max(0, previous + (isOpen ? 1 : -1)));
      setItems(previous => showItem
        ? previous.map(current => current.id === item.id ? item : current)
        : previous.filter(current => current.id !== item.id));
      if (!showItem) setCount(previous => Math.max(0, previous - 1));
    } else {
      if (item.is_task && !item.is_done) setOpenCount(previous => previous + 1);
      if (showItem) {
        setItems(previous => [item, ...previous].slice(0, 50));
        setCount(previous => previous + 1);
      }
    }
  };

  const handleCheckboxChange = async (event, id) => {
    const completed = event.target.checked;
    const originalIndex = items.findIndex(item => item.id === id);
    const original = items[originalIndex];
    if (completed) {
      setItems(previous => previous.filter(item => item.id !== id));
      setCount(previous => Math.max(0, previous - 1));
      setOpenCount(previous => Math.max(0, previous - 1));
    }
    try {
      await updateNote({ id, body: { is_done: completed } }).unwrap();
      if (completed && original) showNotice({ type: "completed", item: original, index: originalIndex });
    } catch {
      if (completed && original) {
        setItems(previous => {
          if (previous.some(item => item.id === id)) return previous;
          const restored = [...previous];
          restored.splice(originalIndex, 0, original);
          return restored;
        });
        setCount(previous => previous + 1);
        setOpenCount(previous => previous + 1);
      }
      setSaveError(true);
    }
  };

  const handleDelete = async id => {
    if (deletingIds.current.has(id)) return;
    const originalIndex = items.findIndex(item => item.id === id);
    const original = items[originalIndex];
    if (!original) return;
    const wasOpen = original.is_task && !original.is_done;
    deletingIds.current.add(id);
    setItems(previous => previous.filter(item => item.id !== id));
    setCount(previous => Math.max(0, previous - 1));
    if (wasOpen) setOpenCount(previous => Math.max(0, previous - 1));
    if (editItem?.id === id) setEditItem(null);
    try {
      await deleteNote(id).unwrap();
      showNotice({ type: "deleted", item: original, index: originalIndex });
    } catch {
      setItems(previous => {
        if (previous.some(item => item.id === id)) return previous;
        const restored = [...previous];
        restored.splice(originalIndex, 0, original);
        return restored;
      });
      setCount(previous => previous + 1);
      if (wasOpen) setOpenCount(previous => previous + 1);
      setSaveError(true);
    } finally {
      deletingIds.current.delete(id);
    }
  };

  const restoreItem = (item, index) => {
    setItems(previous => {
      if (previous.some(current => current.id === item.id)) return previous;
      const restored = [...previous];
      restored.splice(Math.min(index, restored.length), 0, item);
      return restored;
    });
    setCount(previous => previous + 1);
    if (item.is_task && !item.is_done) setOpenCount(previous => previous + 1);
  };

  const undoNotice = async () => {
    if (!notice) return;
    const { type, item, index } = notice;
    setNotice(null);
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    try {
      if (type === "completed") {
        const saved = await updateNote({ id: item.id, body: { is_done: false } }).unwrap();
        restoreItem({ ...item, ...saved, is_done: false }, index);
      } else if (type === "deleted") {
        const records = item.records?.length
          ? item.records
          : item.primary_record
            ? [item.primary_record]
            : [];
        const saved = await createNote({
          content: item.content,
          due_date: item.due_date || null,
          ...recordPayload(records),
        }).unwrap();
        restoreItem({ ...item, ...saved, created_at: saved.date_of_creation || item.created_at }, index);
      }
    } catch {
      setSaveError(true);
    }
  };

  useEffect(() => {
    loadPage(0);
    return () => {
      if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    };
    // The first page is loaded once when the drawer opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emptyMessage =
    filter === "overdue"
      ? "No overdue tasks."
      : filter === "due"
        ? "No open tasks with a due date."
        : "No open tasks yet.";

  return (
    <NotesDrawer isOpen title="Open Tasks" supplementary={`${openCount} ${openCount === 1 ? "task" : "tasks"}`} onClose={onClose}>
      <Layout>
        <TaskComposer
          editingItem={editItem}
          onSave={saveTask}
          onClear={() => setEditItem(null)}
          saving={saving || updating}
        />
        <ScrollRegion>
          {notice && (
            <UndoBar role="status">
              <span>{notice.type === "completed" ? "Completed." : "Deleted."}</span>
              <button type="button" onClick={undoNotice}>Undo</button>
            </UndoBar>
          )}
          <LedgerHeader>
            <LedgerTitle><strong>{count} {count === 1 ? "task" : "tasks"}</strong></LedgerTitle>
            <Filters aria-label="Task filters">
              {FILTERS.map(option => (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={filter === option.id}
                  onClick={() => selectFilter(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </Filters>
          </LedgerHeader>
          {items.length === 0 && !loading && !error && <EmptyState>{emptyMessage}</EmptyState>}
          <NotesList
            listOfItem={items}
            handleCheckboxChange={handleCheckboxChange}
            toggleEditBox={item => setEditItem(current => current?.id === item.id ? null : item)}
            openEditBox={setEditItem}
            deleteNote={handleDelete}
            showRecordLinks
            compactDueDate
            groupByDue
            onRecordOpen={onClose}
          />
          {saveError && <p role="alert">Could not update the task. Please try again.</p>}
          {error && <p role="alert">Could not load open tasks. <button type="button" onClick={() => loadPage(nextOffset ?? 0)}>Try again</button></p>}
          {loading && <p role="status">Loading open tasks…</p>}
          {nextOffset != null && !loading && <button type="button" className="operations-drawer-more" onClick={() => loadPage(nextOffset)}>Load more</button>}
        </ScrollRegion>
      </Layout>
    </NotesDrawer>
  );
}
